import React, { useState } from 'react';
import {
  Grid,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';

import { useApiGet, useApiSend } from '../../../utils/httpClientTs';
import {
  getIntlAppointments,
  sendCommitteeMessage,
} from '../../../endpoints/chapters';

import PositionDisplay from '../../../Components/PositionDisplay';
import { LoadingButton } from '@mui/lab';
import { InternationalCommitteeMessage } from '@/types/chapters';

interface AppointmentMember {
  id: string;
  position: string;
  position_id: string;
  committee_id: string;
  name: string;
  start_date: string;
  end_date: string | null;
  user_id: string;
  order: number;
}

interface Appointment {
  id: string;
  name: string;
  description: string;
  members: AppointmentMember[];
}

const standingApptGroup = 'ffd9f45f6f8f400180bcf25b7ab1afee';

const IntlAppointments: React.FC = () => {
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [errorSaving, setErrorSaving] = useState<string | null>(null);

  const { data: appointmentsData, isFetching } = useApiGet<Appointment[]>(
    ['intl-appointments'],
    getIntlAppointments,
    {
      enabled: true,
      retry: 1,
    },
    {}
  );
  const { mutate: sendMessage, isPending: isSendingMessage } = useApiSend(
    sendCommitteeMessage,
    () => {
      setMessageDialogOpen(false);
      setMessageText('');
      // Show success message
    },
    (error) => {
      setErrorSaving(error?.message || 'Error sending message');
    }
  );
  const handleCloseMessageDialog = () => {
    setMessageDialogOpen(false);
    setMessageText('');
  };

  if (isFetching) {
    return (
      <Grid item xs={12} textAlign={'center'} paddingTop={2}>
        <Typography variant="body1">Loading...</Typography>
      </Grid>
    );
  }

  if (!appointmentsData) {
    return (
      <Grid item xs={12} textAlign={'center'} paddingTop={2}>
        <Typography variant="body1">No data available</Typography>
      </Grid>
    );
  }

  const handleMessageClick = () => {
    setMessageDialogOpen(true);
  };

  const handleSendMessage = () => {
    const messageData: InternationalCommitteeMessage = {
      committee_id: standingApptGroup,
      user_id: null,
      message: messageText.trim(),
    };
    sendMessage(messageData);
  };

  return (
    <Grid container>
      {appointmentsData.map((appointment) => {
        return (
          <Grid item xs={12} md={6} sx={{ padding: 2 }} key={appointment.id}>
            <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
              <CardHeader
                title={
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="h6">{appointment.name}</Typography>
                    {appointment.id === standingApptGroup && (
                      <Button
                        onClick={() => handleMessageClick()}
                        variant="contained"
                        size="small"
                        sx={{
                          fontWeight: 'bold',
                          pb: 0,
                          pt: 0,
                          pl: 1,
                          pr: 1,
                          ml: 1,
                        }}
                      >
                        Message
                      </Button>
                    )}
                  </Box>
                }
                subheader={appointment.description}
                sx={{ p: 1 }}
              />
              <CardContent sx={{ p: 1 }}>
                <Grid container spacing={2}>
                  {appointment.members
                    .sort((a, b) => a.order - b.order)
                    .map((user) => (
                      <PositionDisplay
                        id={user.id}
                        position={user.position}
                        positionId={user.position_id}
                        committeeId={user.committee_id}
                        name={user.name}
                        startDate={user.start_date}
                        endDate={user.end_date}
                        canEdit={false}
                        isSaving={false}
                        errorSavingText={null}
                        onSave={null}
                        userId={user.user_id}
                        pageFrom={'intl-appointments'}
                        canMessage={false}
                      />
                    ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        );
      })}
      {/* Message Dialog */}
      <Dialog
        open={messageDialogOpen}
        onClose={handleCloseMessageDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Send Message to Standing Appointments</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            placeholder="Enter your message..."
            variant="outlined"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          {errorSaving && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              {errorSaving}
            </Typography>
          )}
          <Button onClick={handleCloseMessageDialog}>Cancel</Button>
          <LoadingButton
            onClick={handleSendMessage}
            variant="contained"
            loading={isSendingMessage}
            disabled={!messageText.trim() || isSendingMessage}
          >
            Send Message
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default IntlAppointments;
